import { createIntl, createIntlCache } from "react-intl";

import { ADS_BILLING_COUNTRY_OPTIONS, isAdsBillingCountryCode } from "./AdsBillingUtils";
import { messages } from "./localization/GeoTargetingControls.messages";

import type { IntlShape } from "react-intl";
import type { Option } from "@oai/platform/ui/Select";
import type { components } from "@oai/sofa-client";

const DEFAULT_INTL = createIntl({ locale: "en" }, createIntlCache());

export const BAZAAR_GEO_TARGETING_COUNTRIES_CONFIG = "bazaar_geo_targeting_countries";
export const DEFAULT_GEO_TARGETING_COUNTRIES = ["US"] as const;
export const DEFAULT_GEO_TARGETING_COUNTRY = DEFAULT_GEO_TARGETING_COUNTRIES[0];
export const ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE = "ALL_COUNTRIES";
const GEO_TARGETING_LOCATION_OPTION_PREFIX = "geo-location:";

export type GeoTargetingLocation = {
  id: string;
  name: string | null;
  type: string | null;
  country_code: string | null;
  region_code: string | null;
  canonical_name?: string | null;
  parent_id?: string | null;
};
export type GeoLookupEntry = components["schemas"]["GeoLookupEntry"];

export type GeoTargetingCountryOption = Option & {
  location?: GeoTargetingLocation;
};

export function getAllowedGeoTargetingCountries(configCountries: unknown): string[] {
  const rawCountries =
    typeof configCountries === "string" ? [configCountries]
    : Array.isArray(configCountries) ? configCountries
    : [];

  const normalizedCountries = Array.from(
    new Set(
      rawCountries
        .filter((value): value is string => typeof value === "string")
        .map((value) => value.trim().toUpperCase())
        .filter((value) => value.length > 0 && isAdsBillingCountryCode(value)),
    ),
  );

  const mergedCountries = [
    DEFAULT_GEO_TARGETING_COUNTRY,
    ...normalizedCountries.filter((country) => country !== DEFAULT_GEO_TARGETING_COUNTRY),
  ];
  return mergedCountries.length > 0 ? mergedCountries : [...DEFAULT_GEO_TARGETING_COUNTRIES];
}

export function getDefaultGeoTargetingCountryCodes({
  countryCode,
  allowedGeoTargetingCountries,
}: {
  countryCode: string | null | undefined;
  allowedGeoTargetingCountries: string[];
}): string[] | undefined {
  const normalizedCountryCode = countryCode?.trim().toUpperCase();
  if (
    normalizedCountryCode == null ||
    normalizedCountryCode.length === 0 ||
    !allowedGeoTargetingCountries.includes(normalizedCountryCode)
  ) {
    return undefined;
  }

  return [normalizedCountryCode];
}

export function getGeoTargetingCountryOptions(
  allowedCountries: string[],
  intl: IntlShape = DEFAULT_INTL,
) {
  const regionDisplayNames = new Intl.DisplayNames(intl.locale, { type: "region" });
  const countryOptionByValue: Map<string, (typeof ADS_BILLING_COUNTRY_OPTIONS)[number]> = new Map(
    ADS_BILLING_COUNTRY_OPTIONS.map((option) => [option.value, option]),
  );
  return allowedCountries.flatMap((countryCode) => {
    const option = countryOptionByValue.get(countryCode);
    return option ?
        [{ ...option, label: regionDisplayNames.of(option.value) ?? option.label, disabled: false }]
      : [];
  });
}

export function getGeoTargetingLocationCountryOptions(
  allowedCountries: string[],
  intl: IntlShape = DEFAULT_INTL,
): GeoTargetingCountryOption[] {
  return [
    {
      label: intl.formatMessage(messages.allCountriesOption),
      value: ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE,
    },
    ...getGeoTargetingCountryOptions(allowedCountries, intl),
  ];
}

export function getGeoTargetingLocationOptionValue(locationId: string): string {
  return `${GEO_TARGETING_LOCATION_OPTION_PREFIX}${locationId}`;
}

export function isGeoTargetingLocationOptionValue(value: string): boolean {
  return value.startsWith(GEO_TARGETING_LOCATION_OPTION_PREFIX);
}

export function getGeoTargetingLocationIdFromOptionValue(value: string): string {
  return value.slice(GEO_TARGETING_LOCATION_OPTION_PREFIX.length);
}

export function getGeoTargetingLocationLabel(location: GeoTargetingLocation): string {
  return location.canonical_name ?? location.name ?? location.id;
}

export function toGeoTargetingLocation(entry: GeoLookupEntry): GeoTargetingLocation {
  return {
    id: entry.id,
    name: entry.name,
    type: entry.type,
    country_code: entry.country_code,
    region_code: entry.region_code,
    canonical_name: entry.canonical_name,
    parent_id: entry.parent_id,
  };
}

export function hasIncludedLocationExclusionConflict({
  locationCountryCodes,
  locationGeoLocations,
  excludedLocationCountryCodes,
  excludedLocationGeoLocations,
}: {
  locationCountryCodes: string[];
  locationGeoLocations: GeoTargetingLocation[];
  excludedLocationCountryCodes: string[];
  excludedLocationGeoLocations: GeoTargetingLocation[];
}): boolean {
  const normalizeCountryCode = (countryCode: string | null): string | null => {
    const normalizedCountryCode = countryCode?.trim().toUpperCase();
    return normalizedCountryCode ? normalizedCountryCode : null;
  };
  const normalizedLocationCountryCodes = locationCountryCodes.flatMap((countryCode) => {
    const normalizedCountryCode = normalizeCountryCode(countryCode);
    return normalizedCountryCode ? [normalizedCountryCode] : [];
  });
  const excludedCountryCodeSet = new Set(
    excludedLocationCountryCodes.flatMap((countryCode) => {
      const normalizedCountryCode = normalizeCountryCode(countryCode);
      return normalizedCountryCode ? [normalizedCountryCode] : [];
    }),
  );
  for (const location of excludedLocationGeoLocations) {
    if (location.type?.trim().toLowerCase() !== "country") {
      continue;
    }
    const countryCode = normalizeCountryCode(location.country_code);
    if (countryCode) {
      excludedCountryCodeSet.add(countryCode);
    }
  }

  if (excludedCountryCodeSet.has(ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE)) {
    return normalizedLocationCountryCodes.length > 0 || locationGeoLocations.length > 0;
  }

  const includesAllCountries = normalizedLocationCountryCodes.includes(
    ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE,
  );
  if (
    !includesAllCountries &&
    normalizedLocationCountryCodes.some((countryCode) => excludedCountryCodeSet.has(countryCode))
  ) {
    return true;
  }

  const excludedLocationIdSet = new Set(
    excludedLocationGeoLocations.map((location) => location.id),
  );
  return locationGeoLocations.some((location) => {
    const countryCode = normalizeCountryCode(location.country_code);
    return (
      (countryCode != null && excludedCountryCodeSet.has(countryCode)) ||
      excludedLocationIdSet.has(location.id) ||
      (location.parent_id != null && excludedLocationIdSet.has(location.parent_id))
    );
  });
}

export function toApiGeoTargetingLocation(
  location: GeoTargetingLocation,
): components["schemas"]["TargetingGeoLocationParams"] {
  return { id: location.id };
}

export function getGeoTargetingLocationOptions({
  selectedLocations,
  searchResults,
  enabledCountryCodes,
}: {
  selectedLocations: GeoTargetingLocation[];
  searchResults: GeoLookupEntry[];
  enabledCountryCodes: string[];
}): GeoTargetingCountryOption[] {
  const enabledCountryCodeSet = new Set(enabledCountryCodes);
  const locationsById = new Map<string, GeoTargetingLocation>();
  for (const location of selectedLocations) {
    locationsById.set(location.id, location);
  }
  for (const result of searchResults) {
    locationsById.set(result.id, toGeoTargetingLocation(result));
  }

  return Array.from(locationsById.values()).map((location) => ({
    label: getGeoTargetingLocationLabel(location),
    value: getGeoTargetingLocationOptionValue(location.id),
    location,
    disabled: location.country_code == null || !enabledCountryCodeSet.has(location.country_code),
  }));
}

export function getEnabledGeoTargetingCountryCodes(
  locationCountryOptions: GeoTargetingCountryOption[],
): string[] {
  return locationCountryOptions
    .filter(
      (option) =>
        option.value !== ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE && option.disabled !== true,
    )
    .map((option) => option.value);
}

export function getNextGeoTargetingCountryCodes({
  currentCountryCodes,
  nextCountryCodes,
  enabledCountryCodes,
}: {
  currentCountryCodes: string[];
  nextCountryCodes: string[];
  enabledCountryCodes: string[];
}): string[] {
  const currentIncludesAllCountriesOption = currentCountryCodes.includes(
    ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE,
  );
  const nextIncludesAllCountriesOption = nextCountryCodes.includes(
    ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE,
  );
  const enabledCountryCodeSet = new Set(enabledCountryCodes);
  const currentCountryCodeSet = new Set(currentCountryCodes);
  const selectedEnabledCountryCodes = nextCountryCodes.filter((countryCode) =>
    enabledCountryCodeSet.has(countryCode),
  );
  const selectedEnabledOrPreservedCountryCodes = nextCountryCodes.filter(
    (countryCode) =>
      enabledCountryCodeSet.has(countryCode) || currentCountryCodeSet.has(countryCode),
  );

  if (!nextIncludesAllCountriesOption) {
    return currentIncludesAllCountriesOption ? [] : selectedEnabledOrPreservedCountryCodes;
  }
  if (
    !currentIncludesAllCountriesOption ||
    selectedEnabledCountryCodes.length === enabledCountryCodes.length
  ) {
    return [ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE, ...enabledCountryCodes];
  }

  return selectedEnabledCountryCodes;
}

export function getNextGeoTargetingLocationSelection({
  currentCountryCodes,
  nextOptions,
  enabledCountryCodes,
}: {
  currentCountryCodes: string[];
  nextOptions: GeoTargetingCountryOption[];
  enabledCountryCodes: string[];
}): { countryCodes: string[]; geoLocations: GeoTargetingLocation[] } {
  return {
    countryCodes: getNextGeoTargetingCountryCodes({
      currentCountryCodes,
      nextCountryCodes: nextOptions
        .filter((option) => option.location == null)
        .map((option) => option.value),
      enabledCountryCodes,
    }),
    geoLocations: nextOptions.flatMap((option) => (option.location ? [option.location] : [])),
  };
}

export function normalizeGeoTargetingCountryCodesForForm({
  countryCodes,
  allowedGeoTargetingCountrySet,
  enabledCountryCodes,
  preservedCountryCodes = [],
}: {
  countryCodes: string[];
  allowedGeoTargetingCountrySet: Set<string>;
  enabledCountryCodes: string[];
  preservedCountryCodes?: string[];
}): string[] {
  if (countryCodes.includes(ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE)) {
    return [ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE, ...enabledCountryCodes];
  }
  const preservedCountryCodeSet = new Set(preservedCountryCodes);
  return countryCodes.filter(
    (countryCode) =>
      allowedGeoTargetingCountrySet.has(countryCode) ||
      (preservedCountryCodeSet.has(countryCode) && isAdsBillingCountryCode(countryCode)),
  );
}

export function getTargetingCountryCodesForForm({
  targeting,
  defaultCountryCodes,
}: {
  targeting?: components["schemas"]["CampaignsResource"]["data"]["0"]["targeting"];
  defaultCountryCodes: string[];
}): string[] {
  const countryCodes = targeting?.locations?.countries;
  if (countryCodes != null) {
    return countryCodes;
  }
  if (targeting != null && targeting.locations == null && targeting.excluded_locations == null) {
    return [ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE];
  }
  return defaultCountryCodes;
}

export function toApiGeoTargetingCountryCodes({
  locationCountryCodes,
  enabledCountryCodes,
}: {
  locationCountryCodes: string[];
  enabledCountryCodes: string[];
}): string[] {
  const selectedCountryCodeSet = new Set(
    locationCountryCodes.filter(
      (countryCode) => countryCode !== ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE,
    ),
  );
  const enabledCountryCodeSet = new Set(enabledCountryCodes);
  const shouldUseAllCountriesToken =
    locationCountryCodes.includes(ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE) ||
    (enabledCountryCodes.length > 0 &&
      enabledCountryCodes.every((countryCode) => selectedCountryCodeSet.has(countryCode)) &&
      Array.from(selectedCountryCodeSet).every((countryCode) =>
        enabledCountryCodeSet.has(countryCode),
      ));
  if (shouldUseAllCountriesToken) {
    return [ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE];
  }
  return Array.from(selectedCountryCodeSet);
}

type GeoTargetingTriggerViewProps = {
  values: { label: string; value: string; location?: GeoTargetingLocation }[];
  selectedAll: boolean;
};

export function GeoTargetingCountriesTriggerView(
  { values }: GeoTargetingTriggerViewProps,
  intl: IntlShape = DEFAULT_INTL,
): string {
  if (values.some((value) => value.value === ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE)) {
    return intl.formatMessage(messages.allCountriesTrigger);
  }

  const selectedValues = values.filter((value) => value.value.length > 0);
  if (selectedValues.length === 0) {
    return values[0]?.label ?? intl.formatMessage(messages.selectPlaceholder);
  }
  if (selectedValues.length === 1) {
    return selectedValues[0].label;
  }
  if (selectedValues.every((value) => value.location == null)) {
    return intl.formatMessage(messages.countriesSelected, { count: selectedValues.length });
  }
  return intl.formatMessage(messages.locationsSelected, { count: selectedValues.length });
}

export function GeoTargetingLocationsTriggerView(
  { values }: GeoTargetingTriggerViewProps,
  intl: IntlShape = DEFAULT_INTL,
): string {
  const selectedValues = values.filter((value) => value.value.length > 0);
  if (selectedValues.length === 0) {
    return intl.formatMessage(messages.enterLocationPlaceholder);
  }
  if (selectedValues.length === 1) {
    return selectedValues[0].label;
  }
  return intl.formatMessage(messages.locationsSelected, { count: selectedValues.length });
}

export function getGeoTargetingCountriesTriggerView(intl: IntlShape) {
  return (props: GeoTargetingTriggerViewProps) => GeoTargetingCountriesTriggerView(props, intl);
}

export function getGeoTargetingLocationsTriggerView(intl: IntlShape) {
  return (props: GeoTargetingTriggerViewProps) => GeoTargetingLocationsTriggerView(props, intl);
}
